import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import QRCode from 'qrcode.react'
import React, { useEffect, useRef, useState } from 'react'
import { FaRegClock } from 'react-icons/fa'
import { FaPix } from 'react-icons/fa6'
import { useHistory } from 'react-router'
import { useFinalizeCheckout } from '../../../../../hooks/useFinalizeCheckout'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import PaymentModal from '../../../../../new-components/PaymentModal'
import { getEvents } from '../../../../../services/events'
import { PixTransaction, paymentService } from '../../../../../services/paymentService'
import { WarningBanners } from '../resumeStep/WarningBanners'
import { isProgramOrder } from 'pages/WeightLossProgram/constants'
import { applyPixDiscountToAmount, getPixDiscount } from '@cannect/utils/pixDiscount'
import { paidCheckoutPath, saveProgramPaymentSummary } from 'pages/WeightLossProgram/trail/programCart'

interface PixPaymentProps {
  selectedMethod: string
  cartItems: any[]
  code?: string
  expirationMinutes?: number
  prefetchedTransaction?: PixTransaction | null
  autoGenerate?: boolean
  isFromModal?: boolean
  onPaymentStatusChange?: (status: 'processing' | 'success' | 'error') => void
}

export const PixPayment: React.FC<PixPaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  code = '',
  expirationMinutes = 5,
  prefetchedTransaction = null,
  autoGenerate = true,
  isFromModal = false,
  onPaymentStatusChange
}) => {
  const [timeLeft, setTimeLeft] = useState(expirationMinutes * 60)
  const [pixTransaction, setPixTransaction] = useState<PixTransaction | null>(null)
  const [isGeneratingPix, setIsGeneratingPix] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const { lastOrderCreated } = usePrepareCart() as any
  const history = useHistory()
  const pollingRef = useRef<number | null>(null)
  const pollingTimeoutRef = useRef<number | null>(null)
  const redirectedRef = useRef<boolean>(false)
  const { finalize } = useFinalizeCheckout()
  const { applied: applyPixDiscount, percentage: pixDiscountPercentage } = getPixDiscount(lastOrderCreated)
  // Valor pago no Pix: com o desconto, a menos que o amount do pedido já venha líquido (ms-payments reescreve após o pago).
  const pixPaidAmount = applyPixDiscount
    ? applyPixDiscountToAmount(Number(lastOrderCreated?.amount), pixDiscountPercentage)
    : Number(lastOrderCreated?.amount)


  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const isAllGeneticTests = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.type === 'TESTE_GENETICO'
  )

  const isPillOnly: boolean = lastOrderCreated?.is_pill_only ?? false
  const hasPillControlledItems = (lastOrderCreated?.items || []).some(
    (item: any) => item.requires_controlled_prescription
  )
  const skipDocuments =
    isAllSupplements ||
    isAllGeneticTests ||
    (isPillOnly && !hasPillControlledItems) ||
    isProgramOrder(lastOrderCreated)
  const paidFallback = skipDocuments
    ? `/detalhes-do-pedido/${lastOrderCreated?.id}`
    : `/enviar-receita/${lastOrderCreated?.id}`
  const paidTarget = paidCheckoutPath(lastOrderCreated, paidFallback)

  const showPaymentStatusModal = (status: 'processing' | 'success' | 'error') => {
    setPaymentStatus(status)
    setShowPaymentModal(true)
    onPaymentStatusChange?.(status)
  }

  const handlePaymentModalAction = () => {
    setShowPaymentModal(false)
    if (paymentStatus !== 'success' || !lastOrderCreated?.id) return

    finalize({
      redirectTo: paidTarget,
      shouldClearCart: true,
      alsoClearOrderState: true
    })
  }

  async function generatePix() {
    if (!lastOrderCreated?.id || !lastOrderCreated?.amount) return

    setIsGeneratingPix(true)
    setError(null)

    try {
      const transaction = await paymentService.generatePixCharge(
        lastOrderCreated.id,
        lastOrderCreated.amount,
        applyPixDiscount,
        pixDiscountPercentage
      )
      setPixTransaction(transaction)

      if (transaction.expires_at) {
        const expirationDate = new Date(transaction.expires_at)
        const now = new Date()
        const diffInSeconds = Math.floor((expirationDate.getTime() - now.getTime()) / 1000)
        setTimeLeft(Math.max(0, diffInSeconds))
      }
    } catch (err) {
      setError('Erro ao gerar PIX. Tente novamente.')
      console.error('Erro ao gerar PIX:', err)
    } finally {
      setIsGeneratingPix(false)
    }
  }

  useEffect(() => {
    if (prefetchedTransaction) {
      setPixTransaction(prefetchedTransaction)
      if (prefetchedTransaction.expires_at) {
        const expirationDate = new Date(prefetchedTransaction.expires_at)
        const now = new Date()
        const diffInSeconds = Math.floor((expirationDate.getTime() - now.getTime()) / 1000)
        setTimeLeft(Math.max(0, diffInSeconds))
      }
      return
    }
    if (autoGenerate && lastOrderCreated?.id) {
      ;(async () => {
        await generatePix()
      })()
    }
  }, [lastOrderCreated?.id, prefetchedTransaction, autoGenerate])

  useEffect(() => {
    const chargeId = (pixTransaction as any)?.id || (pixTransaction as any)?.gateway_id
    if (!pixTransaction || !chargeId) return undefined

    const tick = async () => {
      try {
        const status = await paymentService.checkPaymentStatus(String(lastOrderCreated?.id || ''), String(chargeId))
        const last = status?.responses?.[0]?.last_transaction || status?.last_transaction || status

        const paid = last?.charge?.status === 'paid'

        if (paid && lastOrderCreated?.id && !redirectedRef.current) {
          if (pollingRef.current) window.clearInterval(pollingRef.current)
          if (pollingTimeoutRef.current) window.clearTimeout(pollingTimeoutRef.current)
          redirectedRef.current = true

          showPaymentStatusModal('processing')

          setTimeout(() => {
            if (isProgramOrder(lastOrderCreated)) {
              // Programa: a confirmação é a tela da trilha, não o modal genérico.
              saveProgramPaymentSummary({
                orderId: lastOrderCreated?.id,
                method: 'pix',
                amount: pixPaidAmount
              })
            } else {
              showPaymentStatusModal('success')
            }

            getEvents.eventLoadObjects({
              name: 'checkout_redirect',
              category: 'checkout',
              action: 'pix_paid_polling_redirect',
              value: String(lastOrderCreated.id)
            })
            getEvents.eventSendDataLayer({
              eventName: 'checkout_redirect',
              method: isFromModal ? 'card_pix' : 'pix',
              trigger: 'polling_paid',
              orderId: lastOrderCreated.id,
              target: paidTarget
            })

            if (isProgramOrder(lastOrderCreated)) {
              finalize({ redirectTo: paidTarget, shouldClearCart: true, alsoClearOrderState: true })
            }
          }, 4000)
        }
      } catch {
        // ignore
      }
    }

    const id = window.setInterval(tick, 5000)
    pollingRef.current = id

    const timeoutId = window.setTimeout(
      () => {
        if (pollingRef.current !== null) {
          window.clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      },
      20 * 60 * 1000
    )
    pollingTimeoutRef.current = timeoutId

    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      if (pollingTimeoutRef.current !== null) {
        window.clearTimeout(pollingTimeoutRef.current)
        pollingTimeoutRef.current = null
      }
    }
  }, [pixTransaction, lastOrderCreated?.id, isFromModal, history])

  useEffect(() => {
    try {
      const anyPixPaid = (lastOrderCreated?.payments || []).some(
        (p: any) => (p?.payment_method === 'pix' || p?.paymentMethod === 'pix') && p?.status === 'paid'
      )
      if (anyPixPaid && lastOrderCreated?.id && !redirectedRef.current) {
        redirectedRef.current = true
        if (isProgramOrder(lastOrderCreated)) {
          saveProgramPaymentSummary({
            orderId: lastOrderCreated?.id,
            method: 'pix',
            amount: pixPaidAmount
          })
        }
        getEvents.eventLoadObjects({
          name: 'checkout_redirect',
          category: 'checkout',
          action: 'pix_paid_state_redirect',
          value: String(lastOrderCreated.id)
        })
        getEvents.eventSendDataLayer({
          eventName: 'checkout_redirect',
          method: isFromModal ? 'card_pix' : 'pix',
          trigger: 'state_paid',
          orderId: lastOrderCreated.id,
          target: paidTarget
        })
        finalize({
          redirectTo: paidTarget,
          shouldClearCart: true,
          alsoClearOrderState: true
        })
      }
    } catch {
      // ignore
    }
  }, [lastOrderCreated?.payments, lastOrderCreated?.id, isFromModal, history])

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const [copied, setCopied] = useState(false)

  const copyPixCode = () => {
    const pixCode = pixTransaction?.qr_code || pixTransaction?.qr_code_text || code

    const fallbackCopy = (text: string) => {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      } catch {
        // sem suporte a cópia
      }
    }

    const markCopied = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(pixCode)
        .then(markCopied)
        .catch(() => {
          fallbackCopy(pixCode)
          markCopied()
        })
    } else {
      fallbackCopy(pixCode)
      markCopied()
    }
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handleAlreadyPaid = async () => {
    try {
      const chargeId = (pixTransaction as any)?.id || (pixTransaction as any)?.gateway_id
      if (!chargeId || !lastOrderCreated?.id) return
      const status = await paymentService.checkPaymentStatus(String(lastOrderCreated?.id || ''), String(chargeId))
      const last = status?.responses?.[0]?.last_transaction || status?.last_transaction || status

      const paid = last?.charge?.status === 'paid'

      if (paid) {
        getEvents.eventLoadObjects({
          name: 'checkout_redirect',
          category: 'checkout',
          action: 'pix_already_paid_redirect',
          value: String(lastOrderCreated.id)
        })
        getEvents.eventSendDataLayer({
          eventName: 'checkout_redirect',
          method: isFromModal ? 'card_pix' : 'pix',
          trigger: 'already_paid_click',
          orderId: lastOrderCreated.id,
          target: paidTarget
        })
        await finalize({
          redirectTo: paidTarget,
          shouldClearCart: true,
          alsoClearOrderState: true
        })
      }
    } catch {
      // ignore
    }
  }

  if (isGeneratingPix) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          Gerando PIX...
        </h2>
        <div className="mt-6 flex items-center justify-center">
          <div className="h-32 w-32 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-red-700">
          <FaPix className="h-4 w-4 text-red-700" />
          Erro ao gerar PIX
        </h2>
        <p className="mt-2 text-red-600">{error}</p>
        <button
          onClick={generatePix}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Tentar novamente
        </button>
      </div>
    )
  }

  if (!pixTransaction) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          PIX não disponível
        </h2>
        <p className="mt-2 text-gray-600">Clique no botão para gerar o PIX</p>
        <button
          onClick={generatePix}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Gerar PIX
        </button>
      </div>
    )
  }

  return (
    <div className={`mx-auto w-full rounded-xl bg-white p-1 ${isFromModal ? 'p-0' : 'p-8'} shadow-sm`}>
      {isFromModal ? (
        <div>
          <h2 className="text-center text-xl font-semibold text-primary-700">Pague com Pix</h2>
          <p className="mt-1 text-center text-base text-neutral-800">
            Finalize seu pagamento de forma rápida e segura.
          </p>
        </div>
      ) : (
        <>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
            <FaPix className="h-4 w-4 text-primary-700" />
            Pagamento via Pix
          </h2>
          <p className="mt-1 text-sm text-neutral-800">Certo! Pode fazer a leitura do QR Code, por favor?</p>
        </>
      )}

      <div className={`mt-6 rounded-lg ${isFromModal ? 'bg-white' : 'bg-[#FAFAFA]'} p-6`}>
        <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:items-start">
          <div className="flex flex-col items-center">
            {pixTransaction.qr_code_url ? (
              <img
                src={pixTransaction.qr_code_url}
                alt="QR Code PIX"
                width={260}
                height={260}
                style={{ backgroundColor: '#FAFAFA' }}
              />
            ) : (
              <QRCode
                value={pixTransaction.qr_code_text || pixTransaction.qr_code || code}
                size={260}
                includeMargin
                bgColor="#FAFAFA"
              />
            )}
            <div className="mt-3 flex h-8 w-full items-center justify-center gap-1 rounded-md bg-[#F2D4BB] px-4 py-1 text-xs text-neutral-800">
              <FaRegClock className="h-3 w-3 text-neutral-800" />
              Expira em: {formatTime(timeLeft)}
            </div>
          </div>

          <div className="flex flex-col gap-7">
            <h3 className="mb-3 text-sm font-normal text-neutral-800">Siga as etapas abaixo:</h3>
            <ol className="space-y-3 text-sm text-neutral-800">
              <li className="flex items-center gap-2">
                <span className="text-md flex h-7 w-8 items-center justify-center rounded-full border bg-gray-100 font-semibold text-black">
                  1.
                </span>
                Abra seu internet banking ou aplicativo de pagamentos
              </li>
              <li className="flex items-center gap-2">
                <span className="text-md flex h-7 w-7 items-center justify-center rounded-full border bg-gray-100 font-semibold text-black">
                  2.
                </span>
                Acesse pagar via PIX
              </li>
              <li className="flex items-center gap-2">
                <span className="text-md flex h-7 w-7 items-center justify-center rounded-full border bg-gray-100 font-semibold text-black">
                  3.
                </span>
                Cole o código
              </li>
            </ol>

            <div className="mt-6">
              <label htmlFor="pix-code" className="mb-1 block text-sm text-neutral-600">
                Copie o código abaixo
                <input
                  id="pix-code"
                  type="text"
                  value={pixTransaction.qr_code || pixTransaction.qr_code_text || code}
                  readOnly
                  style={{
                    border: '1px solid #B5B3B3'
                  }}
                  className="mt-2 h-10 w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-md bg-[#EDECE7] px-2 py-2 text-base text-gray-700"
                />
              </label>
              <button
                type="button"
                onClick={copyPixCode}
                style={{ minHeight: '44px' }}
                className={`mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border-none px-4 py-3 text-sm font-medium transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-primary-800 text-white hover:bg-primary-700'}`}>
                {copied ? '✓ Código copiado!' : 'Copiar código PIX'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isFromModal ? (
        <div className="mt-6 flex w-full justify-center">
          <button
            type="button"
            onClick={handleAlreadyPaid}
            className="w-full rounded-md border-none bg-primary-800 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700">
            Já paguei!
          </button>
        </div>
      ) : (
        <div className="mt-6">
          <WarningBanners
            containsProductWithLongerDeliveryTime={false}
            containProductsMatirz={false}
            containsProductWithHubDistributor={false}
          />
        </div>
      )}

      {/* Modal de status do pagamento */}
      {showPaymentModal && (
        <PaymentModal
          status={paymentStatus}
          onClose={() => setShowPaymentModal(false)}
          onAction={handlePaymentModalAction}
          isAllSupplements={isAllSupplements}
          isAllGeneticTests={isAllGeneticTests}
          skipDocuments={skipDocuments}
        />
      )}
    </div>
  )
}
